import { useEffect, useState, useCallback, useMemo, useContext, useId } from "react";
import {
    Box,
    Stack,
    Typography,
    MenuItem,
    Card,
    CardContent,
    Avatar,
    Grid
} from "@mui/material";
import { getGridStringOperators } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { userContentReportTableApi } from "../../store/analytics/userContentReport";
import { teacherLoungeSummaryApi } from "../../store/analytics/TeacherLoungeSummary";
import { ThemeContext } from '../../context/ThemeContext';
import NodataImg from "../../assets/images/noData.png";
import dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid';
import CustomGridToolbar from "../../components/CustomGridToolbar";
import { userContent } from "../../store/apiConfigs";
import BackdropUI from "../../ui/Backdrop";
import DataGridComponent from "../../components/DataGridComponent";
import Chart from "../../components/Chart";

const columns_old = [
    {
        field: "username",
        headerName: "Username",
        width: 450,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "fullname",
        headerName: "FullName",
        width: 450,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
        hideable: false
    },
    {
        field: "phone_number",
        headerName: "Phone Number",
        width: 450,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'contains',
        ),
        sortable: false,
        hideable: false
    },
    {
        field: "content_title",
        headerName: "Title",
        width: 450,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
            (operator) => operator.value === 'equals' | operator.value === 'contains',
        ),
        sortable: false,
        hideable: false
    },
    {
        field: "content_type",
        headerName: "Content Type",
        width: 450,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "enrollment_date",
        headerName: "Enrolment date",
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{data?.row?.enrollment_date && dayjs(data?.row?.enrollment_date).format("YYYY-MM-DD HH:mm:ss")}</Typography>
        </>),
        hideable: true
    },
    {
        field: "like",
        headerName: "Like",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "bookmark",
        headerName: "Bookmark",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "grade",
        headerName: "Grade",
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{Number(data?.row?.grade).toFixed(2)}</Typography>
        </>),
        hideable: true
    },
    {
        field: "platform",
        headerName: "Platform",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: true
    },
    {
        field: "passed",
        headerName: "Pass",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: true
    },
    {
        field: "passing_date",
        headerName: "Passing date",
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{data?.row?.passing_date && dayjs(data?.row?.passing_date).format("YYYY-MM-DD HH:mm:ss")}</Typography>
        </>),
        hideable: true
    },
    {
        field: "certificate_generation",
        headerName: "Certificate generation",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: true
    },
    {
        field: "total_time_spent",
        headerName: "Time spent",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: false
    },
];

const columns = [
    {
        field: "id",
        disableColumnMenu: true,
        headerName: "ID",
        width: 120,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "user_id",
        disableColumnMenu: true,
        headerName: "User ID",
        width: 200,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "user_fullname",
        disableColumnMenu: true,
        headerName: "User Fullname",
        width: 250,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "user_profile_image",
        disableColumnMenu: true,
        headerName: "User Profile Image",
        width: 160,
        filterable: false,
        sortable: false,
        hideable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                <Avatar src={params.value} alt="User Profile Image" sx={{ width: 40, height: 40 }} />
            </Box>
        ),
    },
    {
        field: "post_type",
        disableColumnMenu: true,
        headerName: "Post Type",
        width: 180,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "youtube_link",
        disableColumnMenu: true,
        headerName: "YouTube Link",
        width: 250,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "image",
        disableColumnMenu: true,
        headerName: "Image",
        width: 250,
        filterable: false,
        sortable: false,
        hideable: false,
        renderCell: (params) => {
            return (
                params.value &&
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <img
                        src={params.value}
                        alt=""
                        width={100}
                        height={100}
                    />
                </Box>
            )
        },
    },
    {
        field: "thumbnail",
        disableColumnMenu: true,
        headerName: "Thumbnail",
        width: 250,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "description",
        disableColumnMenu: true,
        headerName: "Description",
        width: 300,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "post_approval",
        disableColumnMenu: true,
        headerName: "Post Approval",
        width: 180,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "visibility",
        disableColumnMenu: true,
        headerName: "Visibility",
        width: 180,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "like_count",
        disableColumnMenu: true,
        headerName: "Like Count",
        width: 150,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "comment_count",
        disableColumnMenu: true,
        headerName: "Comment Count",
        width: 160,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "share_count",
        disableColumnMenu: true,
        headerName: "Share Count",
        width: 160,
        filterable: false,
        sortable: false,
        hideable: false
    },
    {
        field: "edit_date",
        disableColumnMenu: true,
        headerName: "Edit Date",
        width: 220,
        filterable: false,
        sortable: false,
        hideable: false,
        renderCell: (data) => (<>
            <Typography sx={{ overflowWrap: "anywhere" }}>{data?.row?.edit_date && dayjs(data?.row?.edit_date).format("YYYY-MM-DD")}</Typography>
        </>)
    },
];
export default function ContentConsumptionNonGraded({ module, monthDaily, region, state, isMaskingEnabled }) {
    const userContentReportTable = useSelector((state) => state?.userContentReportTable);
    const { result: tableResult = [], loading: tableLoading = false, total = 0 } = userContentReportTable || {};
    const dispatch = useDispatch();
    const [isDownload, setIsDownload] = useState(false)
    const [queryOptions, setQueryOptions] = useState({});
    const { paginationModel, setPaginationModel } = useContext(ThemeContext)

    // Teacher Lounge Summary Redux
    const teacherLoungeSummary = useSelector((state) => state?.teacherLoungeSummary);
    const { result: summaryResult } = teacherLoungeSummary || {};

    // Graph Redux
    const communityEngagementGraph = useSelector((state) => state?.communityEngagementGraph);
    const { result: graphResult } = communityEngagementGraph || {};

    const [teacherRegister, setTeacherRegistration] = useState({ l1: true });

    const transformGraphData = useMemo(() => {
        const rawData = graphResult?.data || [];
        if (!Array.isArray(rawData)) return [];

        const isDaily = monthDaily?.toLowerCase() === 'daily';

        if (isDaily) {
            // Build a list of the last 15 days
            return Array.from({ length: 15 }, (_, i) => {
                // Get the date for each day
                const dateObj = dayjs().subtract(14 - i, 'day');

                // Check if we have data for this day from the API
                const match = rawData.find((item) => dayjs(item.date).isSame(dateObj, 'day'));

                // Fill in the data, using 0 for missing days
                return {
                    name: dateObj.format("D MMM"),
                    "No. of posts by teacher": match?.post_count || 0,
                    "Unique Users": match?.unique_users || 0,
                    "Comments by teacher": match?.comment_count || 0,
                    "Like by teacher": match?.like_count || 0,
                };
            });
        }

        return rawData.map(item => ({
            name: dayjs(item.date).format("MMM"),
            "No. of posts by teacher": item.post_count || 0,
            "Unique Users": item.unique_users || 0,
            // "Avg. time spent by teacher": "", not coming from BE
            "Comments by teacher": item?.comment_count || 0,
            "Like by teacher": item.like_count || 0,
        }));
    }, [graphResult, monthDaily]);

    const onFilterChange = useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });
    }, []);
    const getRowSpacing = useCallback((params) => {
        return {
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
        };
    }, []);

    useEffect(() => {
        const query = {
            frequency: monthDaily?.toLowerCase() === 'monthly' ? 'monthly' : 'daily',
            state: state,
            region: region
        };
        dispatch(teacherLoungeSummaryApi({ params: query }));
    }, [dispatch, monthDaily, region, state]);

    useEffect(() => {
        const filterModel = queryOptions?.filterModel;
        const items = filterModel?.items;
        const firstItem = items && items.length > 0 ? items[0] : {};
        const quickFilterValues = filterModel?.quickFilterValues;

        const query = {
            page: paginationModel?.page + 1,
            page_size: paginationModel?.pageSize,
            field: firstItem.field || '',
            module_name: module,
            city: region,
            state: state,
            content_type: module,
            operator: firstItem.operator || (quickFilterValues && "contains") || '',
            value: firstItem.value || (quickFilterValues && quickFilterValues.toString().replaceAll(",", " ")) || ''
        };
        dispatch(userContentReportTableApi(query));
    }, [dispatch, queryOptions, paginationModel, , module, monthDaily, region, state, isMaskingEnabled]);

    return (<>
        <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center" justifyContent={"space-between"}>
            <Typography variant="h3">User Content Report</Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
            <Grid container spacing={1}>
                {summaryResult && Object.entries({
                    posts_by_teacher: summaryResult.posts_by_teacher,
                    comments_by_teacher: summaryResult.comments_by_teacher,
                    likes_by_teacher: summaryResult.likes_by_teacher
                }).map(([key, value], index) => {
                    return (
                        <Grid item xs={6} sm={6} md={6} lg={3} mb={1} key={key}>
                            <Box
                                className="customCard1"
                                sx={{
                                    transition: "none !important",
                                    "&:hover": {
                                        transform: "none !important",
                                    },
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Stack>
                                        <Typography sx={{ textTransform: "capitalize" }}>
                                            {key?.replaceAll("_", " ")}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={600}>
                                            {value ?? 0}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid>
                    )
                })}
            </Grid>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
            <Card sx={{ width: "100%", mb: 2 }}>
                <CardContent>
                    <Chart
                        type={"line"}
                        height={500}
                        data={transformGraphData}
                        module={"communityEngagement"}
                        lineState={teacherRegister}
                    />
                </CardContent>
            </Card>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ my: 2, padding: "10px" }} alignItems="center">
            <DataGridComponent
                loading={tableLoading}
                rowCount={total}
                pageSize={paginationModel?.pageSize}
                onPageSizeChange={(newPageSize) => {
                    setPaginationModel({
                        ...paginationModel,
                        pageSize: newPageSize,
                    })
                }}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sx={{
                    height: 500,
                }}
                getRowId={(row) => `${row.user_id}${row.id}${row.module_id}${row.platform}${uuidv4()}`}
                rows={tableResult || []}
                getRowSpacing={getRowSpacing}
                columns={columns}
                onFilterModelChange={onFilterChange}
                slots={{
                    noRowsOverlay: () => (
                        <Box className="noData">
                            <img src={NodataImg} alt="no data found" />
                        </Box>
                    ),
                    toolbar: (props) =>
                    (CustomGridToolbar({
                        ...props, moduleType: "communityEngagement", queryOptions, paginationModel, isDownload, setIsDownload, apiUrl: userContent, module,
                        region,
                        state,
                        hasfilter: false
                    })),
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                    toolbar: {
                        showQuickFilter: true,
                    },
                    pagination: {
                        labelDisplayedRows: () => null,
                        sx: {
                            "& .MuiTablePagination-actions": {
                                marginLeft: -5
                            }
                        }
                    }
                }}
            />
        </Stack>
        <BackdropUI open={isDownload} />

    </>)
}